import React, { Children } from 'react'
import type { DialogActionsProps as MuiDialogActionsProps } from '@material-ui/core/DialogActions'
import MuiDialogActions from '@material-ui/core/DialogActions'
import type { Theme, WithStyles } from '@material-ui/core/styles'
import { withStyles, createStyles } from '@material-ui/core/styles'
import type { ButtonProps } from '../../Button'
import { cloneChildrenWithClassName, cloneElementWithClassName } from '../../../util'

const styles = (theme: Theme) => {
  const { pxToRem } = theme.typography

  return createStyles({
    root: {
      height: pxToRem(70),
      borderTop: '1px #DDDDDD solid',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2, 3),
        paddingBottom: `calc(${theme.spacing(3)}px + env(safe-area-inset-top))`
      }
    },
    noBorder: {
      borderTop: 'none'
    },
    action: {},
    leftButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      height: '100%'
    },
    leftButtonContainerAligned: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      marginLeft: `-${theme.spacing(1.5)}px`,
      [theme.breakpoints.down('sm')]: {
        marginLeft: `-${theme.spacing(2)}px`
      }
    },
    rightButton: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      width: '100%',
      '& .MuiButtonBase-root': {
        marginLeft: theme.spacing()
      }
    },
    centerButton: {
      display: 'flex',
      justifyContent: 'space-around',
      width: '100%'
    }
  })
}

export interface DialogActionsProps extends WithStyles<typeof styles>, Omit<MuiDialogActionsProps, 'classes'> {
  className?: string
  leftButton?: React.ReactElement<ButtonProps>
  arrows?: React.ReactElement
  showBorder?: boolean
  centeredButton?: boolean
  alignLeftButtonWithTitle?: boolean
}

class DialogActions extends React.PureComponent<DialogActionsProps, any> {
  public static defaultProps: Partial<DialogActionsProps> = {
    showBorder: true
  }

  constructor(props) {
    super(props)
  }

  public render() {
    const {
      className,
      leftButton,
      arrows,
      classes,
      showBorder,
      centeredButton,
      alignLeftButtonWithTitle,
      children,
      ...otherProps
    } = this.props

    // Automatically align left button with title if there's a leftButton and children (confirm button)
    const shouldAlign =
      alignLeftButtonWithTitle !== undefined ? alignLeftButtonWithTitle : !!(leftButton && Children.count(children) > 0)

    return (
      <MuiDialogActions
        className={className}
        {...otherProps}
        classes={{
          root: [classes.root, !showBorder ? classes.noBorder : ''].join(' ')
        }}
        data-testid="dialogActions"
      >
        {leftButton && (
          <div className={shouldAlign ? classes.leftButtonContainerAligned : classes.leftButtonContainer}>
            {cloneElementWithClassName(leftButton, classes.action)}
          </div>
        )}
        {arrows && <div>{cloneElementWithClassName(arrows, classes.action)}</div>}
        <div className={centeredButton ? classes.centerButton : classes.rightButton}>
          {cloneChildrenWithClassName(children, classes.action)}
        </div>
      </MuiDialogActions>
    )
  }
}

export default withStyles(styles)(DialogActions)
